import { IconButton, Stack, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ContractDto,
  CreateContractDto,
  UpdateContractDto,
} from 'tdc-web-backend/contracts/schemas';
import { CompanyRole, EnumProjectContractStatus } from 'tdc-web-backend/enums/enums';
import { ProjectDto } from 'tdc-web-backend/projects/schemas';
import { MilestoneDto, MilestoneSnakeDto } from 'tdc-web-backend/milestones/schemas';
import { ArrowBack, Launch } from '@mui/icons-material';
import { addMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { enUS, hr } from 'date-fns/locale';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info.svg';
import ContractDefaultValues from './utils';
import ContractStepper from '../Sidebar/ContractStepper/ContractStepper';
import ContractCreateModal from './components/ContractCreateModal';
import useCreate from '../../../utils/hooks/crud-hooks/useCreate';
import ContractSignModal from './components/ContractSignModal';
import useUpdate from '../../../utils/hooks/crud-hooks/useUpdate';
import ContractUpdateModal from './components/ContractUpdateModal';
import useUserCompany from '../../../utils/hooks/useUserCompany';
import CompanyInformationForm from './components/CompanyInformationForm';
import ScopeOfWorkForm from './components/ScopeOfWorkForm';
import MilestonesForm from './components/MilestonesForm/MilestonesForm';
import MandatoryLegalForm from './components/MandatoryLegalForm';
import JurisdictionForm from './components/JurisdictionForm';
import SignaturesForm from './components/SignaturesForm';
import ContractEditorFormWrapper from './components/ContractFormWrapper/ContractFormWrapper';
import { ReactComponent as PencilIcon } from '../../../assets/icons/pencil.svg';
import CustomButton from '../../../components/button/CustomButton';
import theme from '../../../utils/theme';
import { simpleDateFormatter } from '../../../utils/helpers';
import CustomTooltip from '../../../components/tooltip/CustomTooltip';
import { primaryDark } from '../../../utils/color';
import useGetScrollIndex from '../../../utils/hooks/useGetScrollIndex';
import useReferenceOne from '../../../utils/hooks/crud-hooks/useReferenceOne';
import { ContractEditorFormSection, ContractEditorProps, DisplayFieldProps } from './interface';
import useGetMany from '../../../utils/hooks/crud-hooks/useGetMany';
import useMode from '../../../utils/hooks/useMode';
import { EnumMode } from '../../../utils/context/Mode.context';

const ContractEditor = ({ sx }: ContractEditorProps) => {
  const { mode, changeMode } = useMode();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpenSign, setIsOpenSign] = useState<boolean>(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState<boolean>(false);
  const [legalRead, setLegalRead] = useState<boolean>(false);
  const navigate = useNavigate();
  const { company } = useUserCompany();
  const { t, i18n } = useTranslation();
  const dateAdapterLocale = i18n.language === 'hr' ? hr : enUS;

  const userType = mode === EnumMode.Client ? CompanyRole.Buyer : CompanyRole.SellerService;

  const { contractId, projectId } = useParams();

  const { data: projectResponse } = useReferenceOne<ProjectDto>({
    resource: 'projects',
    id: projectId,
    enabled: !!projectId && !!company && !!mode,

    onSuccess: (data) => {
      if (data[0]) {
        if (![data[0].buyer, data[0].seller].includes(company?.id as string)) {
          navigate('/dashboard/end-to-end');
        }
        if (data[0].buyer === company?.id && mode === EnumMode.Supplier) {
          changeMode?.(EnumMode.Client);
        }
        if (data[0].seller === company?.id && mode === EnumMode.Client) {
          changeMode?.(EnumMode.Supplier);
        }
      } else {
        navigate('/dashboard/end-to-end');
      }
    },
  });

  const formMethods = useForm<DisplayFieldProps | any>({
    defaultValues: ContractDefaultValues,
    mode: 'all',
  });

  const { setValue, formState, reset, getValues, control, getFieldState, trigger } = formMethods;

  const budgetSum = (milestones: any) =>
    milestones.map((a: any) => a.budget).reduce((a: number, b: number) => a + b);

  const { data: milestonesData } = useGetMany<MilestoneSnakeDto>({
    resource: `milestones/snake?project=${projectId}`,
    enabled: !!projectId && !!company && !!mode,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data } = useReferenceOne<ContractDto>({
    resource: 'contracts',
    id: contractId,
    onSuccess: (data) => {
      const milestones = data?.[0].body.milestones;
      const lastIndex = milestones.length - 1;

      reset(
        {
          ...data?.[0],
          initialAmount: budgetSum(milestones),
          body: {
            ...data?.[0].body,
            projectDurationFrom: simpleDateFormatter(milestones[0].start),
            projectDurationTo: milestones[lastIndex].recurring
              ? simpleDateFormatter(
                  addMonths(
                    new Date(milestones[lastIndex].start),
                    milestones[lastIndex].duration ?? 0,
                  ),
                )
              : simpleDateFormatter(milestones[lastIndex].end),
          },
        },
        { keepTouched: true },
      );
      trigger(
        Array.prototype.concat.apply(
          [],
          formSections.map((section) => section.fieldNames),
        ),
      );
    },
    enabled: contractId !== undefined && !!company && !!mode,
    refetchOnWindowFocus: false,
  });

  const { mutate: create, isLoading } = useCreate<ContractDto, CreateContractDto>({
    resource: '/contracts',
  });

  const { mutate: update, isLoading: isLoadingUpdate } = useUpdate<ContractDto, UpdateContractDto>({
    resource: '/contracts',
  });

  const buyerAccepted = useWatch({
    control,
    name: 'buyerApproved',
  });

  const sellerAccepted = useWatch({
    control,
    name: 'sellerApproved',
  });

  const isLoadingState = isLoading || isLoadingUpdate;

  useEffect(() => {
    if (formState.dirtyFields.body) {
      buyerAccepted && mode === EnumMode.Supplier && setValue('buyerApproved', false);
      sellerAccepted && mode === EnumMode.Client && setValue('sellerApproved', false);
    }
  }, [formState]);

  useEffect(() => {
    if (milestonesData?.data && data) {
      const contractMilestones = getValues('body.milestones');
      const snakeMilestones = milestonesData.data.results;

      contractMilestones?.forEach((milestone: MilestoneDto, index: number) => {
        const related = snakeMilestones.find((m) => m.id === milestone.id);
        if (related) {
          setValue(`body.milestones.${index}.status`, related.status);
        }
      });
    }
  }, [milestonesData, data]);

  const formRefs = useRef<HTMLElement[]>([]);

  const activeScrollIdx = useGetScrollIndex(formRefs.current, 600);

  useEffect(() => {
    if (activeScrollIdx >= 3) {
      setLegalRead(true);
    }
  }, [activeScrollIdx]);

  const handleSubmitCreate = () => {
    const data = getValues();
    contractId
      ? update(
          {
            data,
            id: contractId,
          },
          {
            onSuccess: () => {
              buyerAccepted && sellerAccepted && formState.dirtyFields.body === undefined
                ? setIsOpenSign(false)
                : setIsOpenUpdate(false);
              navigate(`../project/${projectId}`);
            },
          },
        )
      : create(
          {
            ...data,
            project: projectId as string,
          },
          {
            onSuccess: () => {
              setIsOpen(false);
              navigate(`../project/${projectId}`);
            },
          },
        );
  };

  const splitNameForIndex = () => {
    const arrayOfStrings = data?.name.split(' ');
    const annexNumber = arrayOfStrings?.[1];
    return `Annex ${parseInt(annexNumber as string) + 1}`;
  };

  const getFormTitle = () => {
    const name = data?.name;
    const status = data?.status;
    if (
      name === 'Contract' &&
      (status === EnumProjectContractStatus.Active || status === EnumProjectContractStatus.Accepted)
    ) {
      return 'Annex 1';
    }
    if (name !== 'Contract' && status === EnumProjectContractStatus.Accepted) {
      return splitNameForIndex();
    }
    return name;
  };

  const isAnnex = getFormTitle()?.includes('Annex') ?? false;

  const formSections: ContractEditorFormSection[] = [
    {
      title: t('common.companiesDetails'),
      subtitle: t('contracts.companyDetailsDescription'),
      content: (
        <CompanyInformationForm
          disabled={isAnnex || !!projectResponse?.locked}
          userType={userType}
        />
      ),
      fieldNames: ['body.buyer', 'body.seller'],
    },
    {
      title: t('common.projectOverview'),
      subtitle: t('contracts.projectOverviewDescription'),
      content: <ScopeOfWorkForm disabled={isAnnex || projectResponse?.locked} />,
      fieldNames: ['body.scope'],
    },
    {
      title: t('contracts.scopeOfWork'),
      subtitle: t('contracts.scopeOfWorkDescription'),
      content: (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateAdapterLocale}>
          <MilestonesForm disabled={projectResponse?.locked} />
        </LocalizationProvider>
      ),
      fieldNames: ['body.milestones'],
    },
    {
      title: t('common.legal'),
      subtitle: t('contracts.legalDescription'),
      content: <MandatoryLegalForm />,
    },
    {
      title: t('common.jurisdiction'),
      subtitle: t('contracts.jurisdictionDescription'),
      content: <JurisdictionForm disabled={isAnnex || !!projectResponse?.locked} />,
      fieldNames: ['body.jurisdiction'],
      tooltipText: t('contracts.jurisdictionInfo'),
    },
    {
      title: t('contracts.signatures'),
      subtitle: t('contracts.signaturesDescription'),
      content: (
        <SignaturesForm
          userType={userType}
          seller={projectResponse?.seller}
          buyer={projectResponse?.buyer}
          disabeled={projectResponse?.locked}
        />
      ),
      fieldNames: ['buyerApproved', 'sellerApproved'],
    },
  ];

  const isLessThanLarge = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    trigger(
      Array.prototype.concat.apply(
        [],
        formSections.map((section) => section.fieldNames),
      ),
    );
  }, []);

  const SubmitButton = () => (
    <>
      {buyerAccepted &&
      sellerAccepted &&
      formState.dirtyFields.body === undefined &&
      data?.status !== EnumProjectContractStatus.Accepted &&
      data?.status !== EnumProjectContractStatus.Active ? (
        <CustomButton
          onClick={() => setIsOpenSign(true)}
          variant="primary"
          fullWidth
          loading={isLoadingState}
          endIcon={<PencilIcon fill="#fff" />}
          disabled={projectResponse?.locked}
        >
          {t('common.sign')}
        </CustomButton>
      ) : (
        <CustomButton
          variant="primary"
          disabled={
            formState.dirtyFields.body === undefined ||
            !formState.isValid ||
            !legalRead ||
            projectResponse?.locked
          }
          fullWidth
          onClick={() => {
            contractId ? setIsOpenUpdate(true) : setIsOpen(true);
          }}
          endIcon={<Launch />}
          loading={isLoadingState}
        >
          {t('common.submit')}
        </CustomButton>
      )}
    </>
  );

  return (
    <Stack sx={{ ...sx, position: 'relative' }} spacing={15} direction="row">
      <Stack direction="column" spacing={3} width="100%">
        <Stack direction="row" sx={{ alignItems: 'center', pl: 3, pt: 3 }}>
          <IconButton
            sx={{
              border: '1px solid',
              borderColor: 'primaryDark.300',
              mr: 1.5,
              width: 45,
              height: 45,
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowBack sx={{ height: '0.75em' }} />
          </IconButton>
          <Typography variant="heading3" color="primaryDark.300">
            {t('common.back')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={8} sx={{ justifyContent: 'space-between' }}>
          {/* Companies information */}
          <FormProvider {...formMethods}>
            <form>
              <Stack spacing={2}>
                {formSections.map(({ title, subtitle, content, tooltipText }, i) => (
                  <Stack
                    ref={(element: HTMLDivElement | null) =>
                      ((formRefs.current[i] as any) = element)
                    }
                  >
                    <ContractEditorFormWrapper
                      title={title}
                      subtitle={subtitle}
                      titleAdornment={
                        tooltipText ? (
                          <CustomTooltip title={tooltipText}>
                            <InfoIcon fill={primaryDark[500]} />
                          </CustomTooltip>
                        ) : null
                      }
                    >
                      {content}
                    </ContractEditorFormWrapper>
                  </Stack>
                ))}
                <Stack
                  direction="row"
                  sx={{
                    alignSelf: 'end',
                    pt: 3,
                  }}
                />
                <ContractCreateModal
                  handleSubmitCreate={handleSubmitCreate}
                  isOpen={isOpen}
                  isLoading={isLoadingState}
                  setIsOpen={setIsOpen}
                />
                <ContractSignModal
                  handleSubmitCreate={handleSubmitCreate}
                  isOpen={isOpenSign}
                  isLoading={isLoadingState}
                  setIsOpen={setIsOpenSign}
                />
                <ContractUpdateModal
                  isAnnex={isAnnex}
                  handleSubmitCreate={handleSubmitCreate}
                  isOpen={isOpenUpdate}
                  isLoading={isLoadingState}
                  setIsOpen={setIsOpenUpdate}
                />
                <Stack width="160px" alignSelf="end">
                  {isLessThanLarge && <SubmitButton />}
                </Stack>
              </Stack>
            </form>
          </FormProvider>
        </Stack>
      </Stack>
      {!isLessThanLarge && (
        <Stack
          sx={{
            p: 1,
            borderRadius: 0.875,
            bgcolor: 'transparent',
            border: 'none',
            position: 'fixed',
            left: '82%',
            width: '20%',
            ml: '0 !important',
          }}
        >
          <ContractStepper
            onClickStep={(i) => {
              formRefs.current[i].scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }}
            stepperValue={formSections.map((section, i) => ({
              label: section.title,
              completed:
                i === 3
                  ? legalRead
                  : !section.fieldNames?.find((name) => getFieldState(name).invalid),
              active: activeScrollIdx === i,
            }))}
          />
          <Stack
            sx={{
              marginTop: 3.5,
              width: 160,
            }}
          >
            <SubmitButton />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default ContractEditor;
